import type { Locale } from './index'
import { about } from './pages/about'
import { contact } from './pages/contact'
import { help } from './pages/help'
import { home } from './pages/home'
import { legal } from './pages/legal'
import { moved } from './pages/moved'
import { quote } from './pages/quote'
import { services } from './pages/services'
import { track } from './pages/track'

/**
 * Marketing copy in all three locales (en / zh-Hant / zh-Hans).
 *
 * The shared chrome — nav, footer, language switcher and live-chat widget — is
 * defined inline below (extending the mockup's en + zh-Hant `I18N` map in
 * mockups/marketing/site.js with net-new, hand-written Simplified Chinese).
 *
 * Page-body copy lives in per-page fragments under ./pages/* (one file per
 * page, each with the same { en, zh-Hant, zh-Hans } shape) and is merged on top
 * of the chrome below. The fallback chain (locale → en → key) keeps any
 * untranslated key readable meanwhile.
 */
type Messages = Record<string, string>

/** Per-page body-copy fragments, merged into the chrome maps below. */
const fragments = [home, services, about, quote, contact, help, track, legal, moved] as const

const en: Messages = {
  'nav.services': 'Services',
  'nav.about': 'About',
  'nav.track': 'Track',
  'nav.help': 'Help',
  'nav.contact': 'Contact',
  'nav.login': 'Customer login',
  'nav.quote': 'Get a quote',
  'nav.language': 'Language',

  'foot.tagline':
    'An established sea and air freight forwarder. Ocean and air freight, with rail and road — booked and tracked on one platform.',
  'foot.services': 'Services',
  'foot.company': 'Company',
  'foot.support': 'Support',
  'foot.ocean': 'Ocean freight',
  'foot.air': 'Air freight',
  'foot.rail': 'Rail freight',
  'foot.road': 'Road & customs',
  'foot.about': 'About us',
  'foot.track': 'Track a shipment',
  'foot.contact': 'Contact',
  'foot.careers': 'Careers',
  'foot.help': 'Help centre',
  'foot.portal': 'Customer portal',
  'foot.devs': 'Developer API',
  'foot.rights': '© 2026 Geo IV Logistics Ltd. All rights reserved.',
  'foot.privacy': 'Privacy',
  'foot.terms': 'Terms',
  'foot.cookies': 'Cookies',
  'foot.admin': 'Admin login',

  'chat.title': 'Geo4Logistics Support',
  'chat.status': 'Typically replies in a few minutes',
  'chat.hello': 'Hi there 👋 Welcome to Geo4Logistics. How can we help you today?',
  'chat.q1': 'Track a shipment',
  'chat.q2': 'Request a quote',
  'chat.q3': 'Talk to a person',
  'chat.placeholder': 'Write a message…',
  'chat.foot': 'This is a demo chat — no messages are sent.',
  'chat.reply':
    'Thanks! A logistics coordinator will be with you shortly. For instant tracking, you can also use the Track page.',

  'a11y.skip': 'Skip to content',
  'a11y.openMenu': 'Open menu',
  'a11y.closeMenu': 'Close menu',
  'a11y.openChat': 'Chat with support',
  'a11y.closeChat': 'Close chat',
  'a11y.send': 'Send',
  'a11y.email': 'Email',
  'a11y.primaryNav': 'Primary',
  'a11y.brandHome': 'Geo4Logistics — home',

  'cookie.text':
    'We use cookies to run the site, remember your language, and — with your consent — power live chat and analytics.',
  'cookie.accept': 'Accept',
  'cookie.decline': 'Decline',
  'cookie.learn': 'Cookie policy',
  'cookie.label': 'Cookie consent',

  'preview.badge': 'Preview',
  'preview.note': 'This is a live preview — changes go live only when saved.',
}

const zhHant: Messages = {
  'nav.services': '服務',
  'nav.about': '關於我們',
  'nav.track': '貨態查詢',
  'nav.help': '支援中心',
  'nav.contact': '聯絡我們',
  'nav.login': '客戶登入',
  'nav.quote': '取得報價',
  'nav.language': '語言',

  'foot.tagline':
    '成立有年的海空運貨運代理。以海運與空運為主，並提供鐵路與陸運 — 於單一平台完成預訂與追蹤。',
  'foot.services': '服務',
  'foot.company': '公司',
  'foot.support': '支援',
  'foot.ocean': '海運',
  'foot.air': '空運',
  'foot.rail': '鐵路運輸',
  'foot.road': '陸運與報關',
  'foot.about': '關於我們',
  'foot.track': '貨態查詢',
  'foot.contact': '聯絡我們',
  'foot.careers': '人才招募',
  'foot.help': '支援中心',
  'foot.portal': '客戶平台',
  'foot.devs': '開發者 API',
  'foot.rights': '© 2026 Geo IV Logistics Ltd. 版權所有。',
  'foot.privacy': '隱私權',
  'foot.terms': '條款',
  'foot.cookies': 'Cookie',
  'foot.admin': '管理員登入',

  'chat.title': 'Geo4Logistics 客服',
  'chat.status': '通常於數分鐘內回覆',
  'chat.hello': '您好 👋 歡迎來到 Geo4Logistics，有什麼可以為您服務的嗎？',
  'chat.q1': '查詢貨態',
  'chat.q2': '索取報價',
  'chat.q3': '轉接專人',
  'chat.placeholder': '輸入訊息…',
  'chat.foot': '此為示範對話，訊息不會送出。',
  'chat.reply': '感謝您！物流專員將盡快與您聯繫。如需即時追蹤，也可使用「貨態查詢」頁面。',

  'a11y.skip': '跳至內容',
  'a11y.openMenu': '開啟選單',
  'a11y.closeMenu': '關閉選單',
  'a11y.openChat': '與客服對話',
  'a11y.closeChat': '關閉對話',
  'a11y.send': '送出',
  'a11y.email': '電子郵件',
  'a11y.primaryNav': '主要導覽',
  'a11y.brandHome': 'Geo4Logistics — 首頁',

  'cookie.text': '我們使用 Cookie 維持網站運作、記住您的語言，並在您同意後啟用線上客服與分析。',
  'cookie.accept': '接受',
  'cookie.decline': '拒絕',
  'cookie.learn': 'Cookie 政策',
  'cookie.label': 'Cookie 同意',

  'preview.badge': '預覽',
  'preview.note': '這是即時預覽 — 變更需儲存後才會上線。',
}

const zhHans: Messages = {
  'nav.services': '服务',
  'nav.about': '关于我们',
  'nav.track': '货物追踪',
  'nav.help': '帮助中心',
  'nav.contact': '联系我们',
  'nav.login': '客户登录',
  'nav.quote': '获取报价',
  'nav.language': '语言',

  'foot.tagline':
    '深耕多年的海运与空运货运代理。以海运、空运为主，并提供铁路与公路运输 — 在同一平台完成订舱与追踪。',
  'foot.services': '服务',
  'foot.company': '公司',
  'foot.support': '支持',
  'foot.ocean': '海运',
  'foot.air': '空运',
  'foot.rail': '铁路运输',
  'foot.road': '公路与报关',
  'foot.about': '关于我们',
  'foot.track': '货物追踪',
  'foot.contact': '联系我们',
  'foot.careers': '招贤纳士',
  'foot.help': '帮助中心',
  'foot.portal': '客户平台',
  'foot.devs': '开发者 API',
  'foot.rights': '© 2026 Geo IV Logistics Ltd. 保留所有权利。',
  'foot.privacy': '隐私政策',
  'foot.terms': '条款',
  'foot.cookies': 'Cookie',
  'foot.admin': '管理员登录',

  'chat.title': 'Geo4Logistics 客服',
  'chat.status': '通常在几分钟内回复',
  'chat.hello': '您好 👋 欢迎来到 Geo4Logistics，请问有什么可以帮您？',
  'chat.q1': '追踪货物',
  'chat.q2': '索取报价',
  'chat.q3': '转接人工',
  'chat.placeholder': '输入消息…',
  'chat.foot': '这是演示对话，消息不会发送。',
  'chat.reply': '感谢您！物流专员将尽快与您联系。如需即时追踪，也可使用“货物追踪”页面。',

  'a11y.skip': '跳至内容',
  'a11y.openMenu': '打开菜单',
  'a11y.closeMenu': '关闭菜单',
  'a11y.openChat': '与客服对话',
  'a11y.closeChat': '关闭对话',
  'a11y.send': '发送',
  'a11y.email': '电子邮箱',
  'a11y.primaryNav': '主导航',
  'a11y.brandHome': 'Geo4Logistics — 首页',

  'cookie.text': '我们使用 Cookie 维持网站运行、记住您的语言，并在您同意后启用在线客服与分析。',
  'cookie.accept': '接受',
  'cookie.decline': '拒绝',
  'cookie.learn': 'Cookie 政策',
  'cookie.label': 'Cookie 同意',

  'preview.badge': '预览',
  'preview.note': '这是实时预览 — 更改需保存后才会上线。',
}

export const messages: Record<Locale, Messages> = {
  en: Object.assign({}, en, ...fragments.map((f) => f.en)),
  'zh-Hant': Object.assign({}, zhHant, ...fragments.map((f) => f['zh-Hant'])),
  'zh-Hans': Object.assign({}, zhHans, ...fragments.map((f) => f['zh-Hans'])),
}
